import React, { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import Tabs from "@mui/material/Tabs";
import { Tab } from "@verily-src/react-design-system";
import { TabPanel, tabA11yProps } from "components/TabPanel";
import { Role } from "generated/studyauth/studyauth_pb";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { hasAnyRole, selectCurrentStudyRoles } from "core/UserConfigSlice";
import { usePageTitle } from "components/PageTitle";
import ChangeLogsTable from "./ChangeLogsTable";
import {
  setParticipantChangeLogState,
  WhichChangeLogsTable,
} from "./ChangeLogsStateSlice";
import Loading from "components/Loading";

enum TabIndex {
  StudyParticipants = 0,
  ComplianceSettings = 1,
  StudyDetails = 2,
  Sites = 3,
  Users = 4,
}

const ChangeLogsTab: React.FC = () => {
  const currentRegistryId = useAppSelector(
    (state) => state.userConfig.selectedRegistryId
  );

  const selectedRoles = useAppSelector((state) =>
    selectCurrentStudyRoles(state.userConfig)
  );

  const [selectedTabIndex, setSelectedTabIndex] = React.useState(0);
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = React.useState(true);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTabIndex(newValue);
  };

  // Set page title.
  usePageTitle("Change Logs Tab");

  useEffect(() => {
    dispatch(
      setParticipantChangeLogState({
        whichChangeLogsTable:
          WhichChangeLogsTable.STUDY_PARTICIPANTS_BY_REGISTRY_ID,
        registryId: currentRegistryId,
        inTab: true,
      })
    );
    setIsLoading(false);
  }, [currentRegistryId, dispatch]);

  return (
    <>
      {isLoading ? (
        <Loading />
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            padding: "24px 40px 24px 40px",
          }}
        >
          <Box sx={{ marginBottom: "8px" }}>
            <Typography component="h1" variant="display5">
              Data Change Logs
            </Typography>
          </Box>
          <Box sx={{ width: "100%" }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs value={selectedTabIndex} onChange={handleChange}>
                {hasAnyRole(selectedRoles, Role.CDM) && (
                  <Tab
                    label="Study Participants"
                    value={TabIndex.StudyParticipants}
                    {...tabA11yProps(TabIndex.StudyParticipants)}
                    onClick={() =>
                      dispatch(
                        setParticipantChangeLogState({
                          whichChangeLogsTable:
                            WhichChangeLogsTable.STUDY_PARTICIPANTS_BY_REGISTRY_ID,
                          registryId: currentRegistryId,
                          inTab: true,
                        })
                      )
                    }
                  />
                )}
                {hasAnyRole(selectedRoles, Role.CDM) && (
                  <Tab
                    label="Compliance Settings"
                    value={TabIndex.ComplianceSettings}
                    {...tabA11yProps(TabIndex.ComplianceSettings)}
                    onClick={() =>
                      dispatch(
                        setParticipantChangeLogState({
                          whichChangeLogsTable:
                            WhichChangeLogsTable.COMPLIANCE_SETTINGS,
                          registryId: currentRegistryId,
                          inTab: true,
                        })
                      )
                    }
                  />
                )}
                {hasAnyRole(selectedRoles, Role.CDM) && (
                  <Tab
                    label="Study Details"
                    value={TabIndex.StudyDetails}
                    {...tabA11yProps(TabIndex.StudyDetails)}
                    onClick={() =>
                      dispatch(
                        setParticipantChangeLogState({
                          whichChangeLogsTable:
                            WhichChangeLogsTable.STUDY_DETAILS,
                          registryId: currentRegistryId,
                          inTab: true,
                        })
                      )
                    }
                  />
                )}
                {hasAnyRole(selectedRoles, Role.CDM) && (
                  <Tab
                    label="Sites"
                    value={TabIndex.Sites}
                    {...tabA11yProps(TabIndex.Sites)}
                    onClick={() =>
                      dispatch(
                        setParticipantChangeLogState({
                          whichChangeLogsTable: WhichChangeLogsTable.STUDY_SITE,
                          registryId: currentRegistryId,
                          inTab: true,
                        })
                      )
                    }
                  />
                )}
                {hasAnyRole(selectedRoles, Role.CDM) && (
                  <Tab
                    label="Users"
                    value={TabIndex.Users}
                    {...tabA11yProps(TabIndex.Users)}
                    onClick={() =>
                      dispatch(
                        setParticipantChangeLogState({
                          whichChangeLogsTable:
                            WhichChangeLogsTable.USERS_CDM_OR_SPONSOR_OPS_VIEW,
                          registryId: currentRegistryId,
                          inTab: true,
                        })
                      )
                    }
                  />
                )}
              </Tabs>
            </Box>
            {
              <>
                <TabPanel
                  selectedTabIndex={selectedTabIndex}
                  tabIndex={TabIndex.StudyParticipants}
                >
                  <ChangeLogsTable />
                </TabPanel>
                <TabPanel
                  selectedTabIndex={selectedTabIndex}
                  tabIndex={TabIndex.ComplianceSettings}
                >
                  <ChangeLogsTable />
                </TabPanel>

                <TabPanel
                  selectedTabIndex={selectedTabIndex}
                  tabIndex={TabIndex.StudyDetails}
                >
                  <ChangeLogsTable />
                </TabPanel>

                <TabPanel
                  selectedTabIndex={selectedTabIndex}
                  tabIndex={TabIndex.Sites}
                >
                  <ChangeLogsTable />
                </TabPanel>

                <TabPanel
                  selectedTabIndex={selectedTabIndex}
                  tabIndex={TabIndex.Users}
                >
                  <ChangeLogsTable />
                </TabPanel>
              </>
            }
          </Box>
        </Box>
      )}
    </>
  );
};

export default ChangeLogsTab;
